import type { Quest } from 'types/quests'

const STREAM_QUEST_ROW_CONTEXT_FIELDS = [
    'stream_item_id',
    'notification_context',
    'matching_answer_ids',
    'notification_reason_label',
    'feed_reason_label',
    'not_acted_answer_ids',
    'has_acted',
    'noti_ids',
    'feed_card',
] as const

type StreamQuestRowContextField = (typeof STREAM_QUEST_ROW_CONTEXT_FIELDS)[number]

export type StreamQuestRowRef = Pick<Quest, 'id'> & Partial<Pick<Quest, StreamQuestRowContextField>>

function hasOwnKey(value: unknown, key: string): value is Record<string, unknown> {
    return !!value && Object.prototype.hasOwnProperty.call(value, key)
}

function cloneContextValue<T>(value: T): T {
    if (Array.isArray(value)) return [...value] as T
    if (value && typeof value === 'object') return { ...value } as T
    return value
}

function contextFieldsPresent(value: unknown): boolean {
    return STREAM_QUEST_ROW_CONTEXT_FIELDS.some(field => hasOwnKey(value, field))
}

function contextValueEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) return true

    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right)) return false
        if (left.length !== right.length) return false
        return left.every((entry, index) => contextValueEqual(entry, right[index]))
    }

    if (left && right && typeof left === 'object' && typeof right === 'object') {
        const leftRecord = left as Record<string, unknown>
        const rightRecord = right as Record<string, unknown>
        const leftKeys = Object.keys(leftRecord)
        const rightKeys = Object.keys(rightRecord)
        return (
            leftKeys.length === rightKeys.length &&
            leftKeys.every(
                key =>
                    Object.prototype.hasOwnProperty.call(rightRecord, key) &&
                    contextValueEqual(leftRecord[key], rightRecord[key]),
            )
        )
    }

    return false
}

export function streamQuestRowRefsEqual(
    left: StreamQuestRowRef | null | undefined,
    right: StreamQuestRowRef | null | undefined,
): boolean {
    if (left === right) return true
    if (!left || !right) return false

    const leftRecord = left as Record<string, unknown>
    const rightRecord = right as Record<string, unknown>
    const leftKeys = Object.keys(leftRecord)
    const rightKeys = Object.keys(rightRecord)
    return (
        leftKeys.length === rightKeys.length &&
        leftKeys.every(
            key =>
                Object.prototype.hasOwnProperty.call(rightRecord, key) &&
                contextValueEqual(leftRecord[key], rightRecord[key]),
        )
    )
}

function contextValuesMatch(cachedQuest: Quest, row: StreamQuestRowRef): boolean {
    const cachedQuestRecord = cachedQuest as unknown as Record<string, unknown>
    const rowRecord = row as unknown as Record<string, unknown>

    return STREAM_QUEST_ROW_CONTEXT_FIELDS.every(field => {
        if (!hasOwnKey(row, field)) return true
        return contextValueEqual(cachedQuestRecord[field], rowRecord[field])
    })
}

function normalizedIdList(value: unknown): string[] {
    return Array.isArray(value)
        ? value
              .map(entry => (entry == null ? null : String(entry)))
              .filter((entry): entry is string => !!entry)
        : []
}

function normalizedLabel(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

export function buildStreamQuestRowRef(quest: Quest): StreamQuestRowRef {
    const row = { id: quest.id } as StreamQuestRowRef

    STREAM_QUEST_ROW_CONTEXT_FIELDS.forEach(field => {
        if (!hasOwnKey(quest, field)) return
        ;(row as Record<string, unknown>)[field] = cloneContextValue(
            (quest as unknown as Record<string, unknown>)[field],
        )
    })

    return row
}

export function stripStreamQuestRowContext(quest: Quest): Quest {
    const cleanQuest = { ...quest } as Quest & Record<string, unknown>
    STREAM_QUEST_ROW_CONTEXT_FIELDS.forEach(field => {
        delete cleanQuest[field]
    })
    return cleanQuest
}

// Merged rows are memoized per (cachedQuest, row) object pair. Both inputs are immutable:
// cache writes replace the normalized quest entity (state/cache dependency invalidation) and
// refetches replace the row refs, so identity of the merged quest rotates exactly when its
// content or row context changes — which is what downstream geometry memos key on.
const mergedRowContextCache = new WeakMap<Quest, WeakMap<StreamQuestRowRef, Quest>>()

export function applyStreamQuestRowContext(
    cachedQuest: Quest | null | undefined,
    row: StreamQuestRowRef | null | undefined,
): Quest | null {
    if (!cachedQuest) return null
    if (!contextFieldsPresent(row)) return cachedQuest
    if (contextValuesMatch(cachedQuest, row)) return cachedQuest

    let mergedByRow = mergedRowContextCache.get(cachedQuest)
    if (!mergedByRow) {
        mergedByRow = new WeakMap()
        mergedRowContextCache.set(cachedQuest, mergedByRow)
    }
    const memoizedQuest = row ? mergedByRow.get(row) : undefined
    if (memoizedQuest) return memoizedQuest

    const quest = { ...cachedQuest } as Quest
    STREAM_QUEST_ROW_CONTEXT_FIELDS.forEach(field => {
        if (!hasOwnKey(row, field)) return
        ;(quest as unknown as Record<string, unknown>)[field] = cloneContextValue(
            (row as unknown as Record<string, unknown>)[field],
        )
    })

    if (row) mergedByRow.set(row, quest)
    return quest
}

// Reuse the previous array identity when the next render derived an element-wise identical
// list, so list-level memos only recompute when a row actually changed.
export function preserveStreamQuestArrayIdentity<T>(previous: T[], next: T[]): T[] {
    if (previous === next) return previous
    if (!Array.isArray(previous) || previous.length !== next.length) return next

    for (let index = 0; index < next.length; index += 1) {
        if (!Object.is(previous[index], next[index])) return next
    }

    return previous
}

export function streamQuestRowKey(
    quest:
        | Pick<
              Quest,
              | 'id'
              | 'stream_item_id'
              | 'matching_answer_ids'
              | 'notification_reason_label'
              | 'feed_reason_label'
              | 'noti_ids'
          >
        | null
        | undefined,
    fallbackIndex?: number,
): string {
    const questId = quest?.id != null ? String(quest.id) : `index-${fallbackIndex ?? 'unknown'}`
    const streamItemId = normalizedLabel(quest?.stream_item_id)
    if (streamItemId) return streamItemId

    const notiIds = normalizedIdList(quest?.noti_ids)
    if (notiIds.length > 0) return `quest:${questId}:noti:${notiIds.join(',')}`

    const matchingAnswerIds = normalizedIdList(quest?.matching_answer_ids)
    if (matchingAnswerIds.length > 0) {
        return `quest:${questId}:answers:${matchingAnswerIds.join(',')}`
    }

    const label =
        normalizedLabel(quest?.notification_reason_label) ||
        normalizedLabel(quest?.feed_reason_label)
    if (label) return `quest:${questId}:label:${label}`

    return `quest:${questId}`
}

// Page-number pagination over an activity-sorted feed can return the same quest on more
// than one page when items reorder between fetches. Collapse repeats, keeping the first
// (higher) occurrence so the dropped copy is always below where the user has scrolled and
// the scroll position is preserved.
//
// - Activity feeds show a quest once: dedupe on the quest id, and also drop the pinned
//   quest (it renders separately above the body).
// - Notification streams may legitimately surface the same quest for several reasons:
//   dedupe on the row identity (`streamQuestRowKey`) so distinct surfaces survive while
//   exact repeats are still collapsed.
export function dedupeStreamQuestRows(
    rows: StreamQuestRowRef[],
    { keyByRow = false, pinnedId = null }: { keyByRow?: boolean; pinnedId?: string | null } = {},
): StreamQuestRowRef[] {
    const seen = new Set<string>()

    return rows.filter(row => {
        const dedupeKey = keyByRow
            ? streamQuestRowKey(row)
            : row?.id != null
            ? String(row.id)
            : null

        if (!dedupeKey) return true
        if (!keyByRow && pinnedId != null && dedupeKey === pinnedId) return false
        if (seen.has(dedupeKey)) return false

        seen.add(dedupeKey)
        return true
    })
}
